import React, { useState, useEffect } from 'react';

const API = 'https://stock-api-production-913a.up.railway.app';
const getAdminToken = () => sessionStorage.getItem('wintime_admin_token') || '';

// ─── 股市MLB 12 項指標，依三大面向分組 ───
const FACE_GROUPS = [
  {
    face: 'safety', icon: '🛡️', label: '安全性', color: '#4C9BB8',
    keys: ['scoring_safety_1', 'scoring_safety_2', 'scoring_safety_3', 'scoring_safety_4'],
  },
  {
    face: 'profit', icon: '💰', label: '獲利性', color: '#f5c518',
    keys: ['scoring_profit_1', 'scoring_profit_2', 'scoring_profit_3', 'scoring_profit_4', 'scoring_profit_5'],
  },
  {
    face: 'future', icon: '🚀', label: '未來性', color: '#e54848',
    keys: ['scoring_future_1', 'scoring_future_2', 'scoring_future_3'],
  },
];

// ─── 每張卡片內的四類清單設定 ───
const LIST_TYPES = [
  { field: 'positives', label: '加分情境', icon: '🟢', color: '#4ec94e' },
  { field: 'negatives', label: '扣分情境', icon: '🔴', color: '#e05c5c' },
  { field: 'warnings',  label: '警告情境', icon: '⚠️', color: '#f0a500' },
  { field: 'notes',     label: '行業注意事項', icon: '💡', color: '#7ab0cc' },
];

export default function RulesManager() {
  const [tab, setTab] = useState('personal'); // 'personal' | 'mlb'

  // 個股總覽分析規則（原本的大文字框）
  const [rules, setRules]     = useState('');
  const [loading, setLoading] = useState(true);  // 個股總覽規則載入中
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  // 股市MLB 評分規則（結構化）
  const [scoringRules, setScoringRules] = useState([]); // [{rule_key,title,warnings,positives,negatives,notes}]
  const [mlbLoading, setMlbLoading]     = useState(false);
  const [mlbMsg, setMlbMsg]             = useState({}); // { rule_key: '✅ 已儲存', _global: '...' }
  const [savingKey, setSavingKey]       = useState(''); // 目前正在儲存的指標 key

  // ── 進入頁面時載入個股總覽規則（登入已由 App.js 的管理者密碼閘門處理）──
  useEffect(() => {
    fetch(`${API}/api/analysis-rules?pw=${encodeURIComponent(getAdminToken())}`)
      .then(r => r.json())
      .then(d => {
        if (d.ok) setRules(d.content || '');
        else setMsg('❌ ' + (d.error || '載入失敗'));
      })
      .catch(() => setMsg('❌ 連線失敗'))
      .finally(() => setLoading(false));
  }, []);

  // ── 個股總覽規則：儲存 ──
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${API}/api/analysis-rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: rules, pw: getAdminToken() }),
      });
      const data = await res.json();
      setMsg(data.ok ? '✅ 儲存成功！' : '❌ 儲存失敗');
    } catch (e) { setMsg('❌ 連線失敗'); }
    setSaving(false);
  };

  // ── 股市MLB 規則：從雲端載入 ──
  const loadScoringRules = async () => {
    setMlbLoading(true); setMlbMsg({});
    try {
      const res = await fetch(`${API}/api/scoring-rules?token=${encodeURIComponent(getAdminToken())}`);
      const data = await res.json();
      if (data.ok) {
        const norm = (data.rules || []).map(r => ({
          rule_key: r.rule_key,
          title: r.title || '',
          warnings:  Array.isArray(r.warnings)  ? r.warnings  : [],
          positives: Array.isArray(r.positives) ? r.positives : [],
          negatives: Array.isArray(r.negatives) ? r.negatives : [],
          notes:     Array.isArray(r.notes)     ? r.notes     : [],
        }));
        setScoringRules(norm);
      } else {
        setMlbMsg({ _global: '❌ 載入失敗：' + (data.error || '未知錯誤') });
      }
    } catch (e) {
      setMlbMsg({ _global: '❌ 連線失敗' });
    }
    setMlbLoading(false);
  };

  // ── 切換分頁；第一次切到 MLB 分頁時自動載入 ──
  const switchTab = (t) => {
    setTab(t);
    if (t === 'mlb' && scoringRules.length === 0 && !mlbLoading) {
      loadScoringRules();
    }
  };

  // ── 修改某指標、某清單、某條目的文字 ──
  const updateItem = (ruleKey, field, idx, value) => {
    setScoringRules(prev => prev.map(r => {
      if (r.rule_key !== ruleKey) return r;
      const list = [...r[field]];
      list[idx] = value;
      return { ...r, [field]: list };
    }));
  };

  // ── 在某清單新增一個空白條目 ──
  const addItem = (ruleKey, field) => {
    setScoringRules(prev => prev.map(r => {
      if (r.rule_key !== ruleKey) return r;
      return { ...r, [field]: [...r[field], ''] };
    }));
  };

  // ── 刪除某清單的某條目 ──
  const removeItem = (ruleKey, field, idx) => {
    setScoringRules(prev => prev.map(r => {
      if (r.rule_key !== ruleKey) return r;
      return { ...r, [field]: r[field].filter((_, i) => i !== idx) };
    }));
  };

  // ── 儲存某一項指標的規則 ──
  const saveScoringRule = async (rule) => {
    setSavingKey(rule.rule_key);
    setMlbMsg(prev => ({ ...prev, [rule.rule_key]: '' }));
    try {
      const res = await fetch(`${API}/api/admin/scoring-rules/save?token=${encodeURIComponent(getAdminToken())}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rule_key: rule.rule_key,
          title: rule.title,
          warnings:  rule.warnings.map(s => s.trim()).filter(Boolean),
          positives: rule.positives.map(s => s.trim()).filter(Boolean),
          negatives: rule.negatives.map(s => s.trim()).filter(Boolean),
          notes:     rule.notes.map(s => s.trim()).filter(Boolean),
        }),
      });
      const data = await res.json();
      setMlbMsg(prev => ({
        ...prev,
        [rule.rule_key]: data.ok ? '✅ 已儲存' : '❌ ' + (data.error || '儲存失敗'),
      }));
    } catch (e) {
      setMlbMsg(prev => ({ ...prev, [rule.rule_key]: '❌ 連線失敗' }));
    }
    setSavingKey('');
  };

  // 分頁按鈕樣式
  const tabBtn = (active, color) => ({
    padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontWeight: 700, fontSize: 14,
    background: active ? color : '#1a2535',
    color: active ? '#fff' : '#7a8a9a',
  });

  // ════════ 主畫面（登入由 App.js 統一處理，這裡不再有登入畫面）════════
  return (
    <div style={{ padding: '16px', maxWidth: 1100, margin: '0 auto' }}>
      {/* 分頁切換 */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => switchTab('personal')} style={tabBtn(tab === 'personal', '#4C9BB8')}>
          📋 個股總覽分析規則
        </button>
        <button onClick={() => switchTab('mlb')} style={tabBtn(tab === 'mlb', '#f5c518')}>
          🏆 股市MLB評分規則
        </button>
      </div>

      {/* ──────── 分頁1：個股總覽分析規則（原本的大文字框） ──────── */}
      {tab === 'personal' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
            gap: 8, marginBottom: 12 }}>
            {msg && <span style={{ color: msg.startsWith('✅') ? '#4ec94e' : '#e05c5c', fontSize: 14 }}>{msg}</span>}
            <button onClick={save} disabled={saving || loading}
              style={{ padding: '8px 24px', borderRadius: 8, border: 'none',
                background: (saving || loading) ? '#555' : '#4ec94e', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
              {saving ? '儲存中...' : '💾 儲存'}
            </button>
          </div>
          <div style={{ background: '#1a2535', borderRadius: 8, padding: '12px 16px',
            marginBottom: 12, fontSize: 13, color: '#aaa' }}>
            📝 可直接在下方編輯、新增或刪除規則，編輯完畢後按「儲存」即自動更新到雲端。
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#7ab0cc' }}>⏳ 載入規則中...</div>
          ) : (
            <>
              <textarea
                value={rules}
                onChange={e => setRules(e.target.value)}
                style={{ width: '100%', height: 'calc(100vh - 290px)', padding: '16px',
                  background: '#151f2e', color: '#ddd', border: '1px solid #2a3a4a',
                  borderRadius: 8, fontSize: 14, lineHeight: 1.8, fontFamily: 'monospace',
                  resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ marginTop: 8, color: '#555', fontSize: 12 }}>字數：{rules.length} 字元</div>
            </>
          )}
        </>
      )}

      {/* ──────── 分頁2：股市MLB評分規則（結構化編輯） ──────── */}
      {tab === 'mlb' && (
        <>
          <div style={{ background: '#1a2535', borderRadius: 8, padding: '12px 16px',
            marginBottom: 16, fontSize: 13, color: '#aaa', lineHeight: 1.7 }}>
            🏆 股市MLB 12 項指標的評分標準。每一項分成「加分 / 扣分 / 警告 / 注意」四類，每類都可以自由新增或刪除條目。
            AI 評分時會依照這些標準打分數，<b style={{ color: '#f5c518' }}>改完記得按該指標卡片上的「儲存」鈕</b>。
          </div>

          {mlbLoading && (
            <div style={{ padding: 40, textAlign: 'center', color: '#7ab0cc' }}>⏳ 載入評分規則中...</div>
          )}

          {mlbMsg._global && (
            <div style={{ padding: 16, color: '#e05c5c' }}>
              {mlbMsg._global}
              <button onClick={loadScoringRules}
                style={{ marginLeft: 10, padding: '4px 12px', borderRadius: 6,
                  border: '1px solid #555', background: 'transparent', color: '#aaa', cursor: 'pointer' }}>
                重試
              </button>
            </div>
          )}

          {!mlbLoading && scoringRules.length > 0 && FACE_GROUPS.map(group => (
            <div key={group.face} style={{
              marginBottom: 18, background: '#0d1520',
              border: `1px solid ${group.color}40`, borderRadius: 10, padding: 16,
            }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: group.color, marginBottom: 14,
                paddingBottom: 10, borderBottom: `1px solid ${group.color}30` }}>
                {group.icon} {group.label}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {group.keys.map(rk => {
                  const rule = scoringRules.find(r => r.rule_key === rk);
                  if (!rule) return null;
                  const itemMsg = mlbMsg[rk];
                  return (
                    <div key={rk} style={{ background: '#080d14', borderRadius: 8,
                      padding: 16, border: '1px solid #1a2535' }}>
                      {/* 指標標題 + 儲存鈕 */}
                      <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: 12 }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#ddd' }}>{rule.title}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {itemMsg && <span style={{ fontSize: 13,
                            color: itemMsg.startsWith('✅') ? '#4ec94e' : '#e05c5c' }}>{itemMsg}</span>}
                          <button onClick={() => saveScoringRule(rule)} disabled={savingKey === rk}
                            style={{ padding: '6px 18px', borderRadius: 6, border: 'none',
                              background: savingKey === rk ? '#555' : '#4ec94e', color: '#fff',
                              fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                            {savingKey === rk ? '儲存中...' : '💾 儲存'}
                          </button>
                        </div>
                      </div>

                      {/* 四類清單 */}
                      <div style={{ display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                        {LIST_TYPES.map(lt => (
                          <div key={lt.field} style={{ background: '#0d1520', borderRadius: 6, padding: 12 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: lt.color, marginBottom: 8 }}>
                              {lt.icon} {lt.label}
                            </div>
                            {rule[lt.field].length === 0 && (
                              <div style={{ fontSize: 12, color: '#4a5a6a',
                                fontStyle: 'italic', marginBottom: 8 }}>（目前無條目）</div>
                            )}
                            {rule[lt.field].map((text, idx) => (
                              <div key={idx} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                                <input
                                  value={text}
                                  onChange={e => updateItem(rk, lt.field, idx, e.target.value)}
                                  placeholder="輸入一條規則..."
                                  style={{ flex: 1, padding: '6px 8px', borderRadius: 5,
                                    border: '1px solid #2a3a4a', background: '#151f2e', color: '#ddd',
                                    fontSize: 12, boxSizing: 'border-box' }}
                                />
                                <button onClick={() => removeItem(rk, lt.field, idx)}
                                  style={{ padding: '0 8px', borderRadius: 5, border: 'none',
                                    background: '#2a1a1a', color: '#e05c5c', cursor: 'pointer', fontSize: 13 }}>
                                  ✕
                                </button>
                              </div>
                            ))}
                            <button onClick={() => addItem(rk, lt.field)}
                              style={{ width: '100%', padding: '5px', borderRadius: 5,
                                border: `1px dashed ${lt.color}66`, background: 'transparent',
                                color: lt.color, cursor: 'pointer', fontSize: 12, marginTop: 2 }}>
                              + 新增
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
